import { Link } from '@tanstack/react-router';
import { useApiProblem } from '@/shared/api';
import { Button } from '@/shared/components';
import { formatMoney } from '@/shared/lib/format';

interface Props {
  nights: number;
  rate: number;
  currency: string | null;
  error: unknown;
  pending: boolean;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

/** Codes, deren Text der Server nicht auf Deutsch mitschickt. */
const TEXT: Record<string, string> = {
  property_not_found: 'Dieses Apartment gibt es in diesem Workspace nicht mehr.',
  tenant_missing: 'Die Anmeldung gehört zu keinem Workspace – bitte neu anmelden.',
};

/** Fester Fuß des Panels: Summe, Grund einer Ablehnung, Abbrechen und Anlegen. */
export function ManualBookingFooter({
  nights,
  rate,
  currency,
  error,
  pending,
  canSubmit,
  onCancel,
  onSubmit,
}: Props) {
  const problem = useApiProblem(error);
  const reason = problem
    ? (TEXT[problem.title] ??
      problem.detail ??
      `Die Buchung wurde nicht angelegt (Code ${problem.title}).`)
    : null;
  const total = nights > 0 && rate > 0 && currency;
  return (
    <div className="flex flex-col gap-2.5">
      {reason && (
        <p role="alert" className="bg-sealwax-tint text-ink rounded-control px-3 py-2 text-[13px]">
          {reason}{' '}
          {problem?.title === 'stripe_not_connected' && (
            <Link to="/settings/stripe" className="text-forest font-bold">
              Zu Stripe
            </Link>
          )}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-ink-soft min-w-0 flex-1 text-[13px]">
          Gesamt
          {total
            ? ` · ${nights} ${nights === 1 ? 'Nacht' : 'Nächte'} × ${formatMoney(rate, currency)}`
            : ''}{' '}
          <b className="text-ink font-serif text-lg">
            {total ? formatMoney(rate * nights, currency) : '—'}
          </b>
        </p>
        <Button variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button variant="brand" loading={pending} disabled={!canSubmit} onClick={onSubmit}>
          Buchung anlegen
        </Button>
      </div>
    </div>
  );
}
